import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { SiteMembership } from '@se-po/shared-data-access-models'
import { SiteMembershipService, SurveyFindAllParams, SurveyService, SurveyStatus } from '@se-po/shared-data-access-services'
import { forkJoin, Observable, of, Subscription, timer } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { Survey } from 'se-resource-types/dist/lib/Ngin'
import {
  OpenRegistrationFilterOption,
  OpenRegistrationFilters,
} from '../open-registration-filter/open-registration-filter.component'

export interface OpenRegistrationOrganization {
  organizationId: string | number
  organizationName: string
  organizationColor: string
  organizationLogoUrl: string
  registrations: Survey[]
}

type SurveyWithSportType = Survey & {
  sport_key?: string
  signup_type_list?: string[]
}

@Component({
  selector: 'se-open-registration-list',
  templateUrl: './open-registration-list.component.html',
  styleUrls: ['./open-registration-list.component.scss'],
  standalone: false,
})
export class OpenRegistrationListComponent
  implements OnDestroy, OnInit
{
  public registrationGroups: OpenRegistrationOrganization[] = []
  public loadedRegistrationGroups: OpenRegistrationOrganization[] = []
  public organizations: OpenRegistrationFilterOption[] = []
  public sportOptions: OpenRegistrationFilterOption[] = []
  public signupTypeList: OpenRegistrationFilterOption[] = []
  public hasLoaded = false
  public isLoading = false
  public loadError = false
  public activeSports: string[] = []
  public activeTypes: string[] = []
  public activeRegistrationNameLike = ''
  public appliedOrganizationIdFilters: string[] = []

  private siteMembershipService = inject(SiteMembershipService)
  private surveyService = inject(SurveyService)
  private registrationsSubscription?: Subscription
  private searchTermSubscription?: Subscription
  private hasLoadedRegistrationGroups = false

  public ngOnInit(): void {
    this.loadedRegistrationGroups = []
    this.hasLoadedRegistrationGroups = false
    this.loadRegistrations()
  }

  public ngOnDestroy(): void {
    this.registrationsSubscription?.unsubscribe()
    this.searchTermSubscription?.unsubscribe()
  }

  public get filteredRegistrationGroups(): OpenRegistrationOrganization[] {
    return this.registrationGroups
  }

  public registrationSportType(registration: Survey): string {
    const registrationWithSportType = registration as SurveyWithSportType

    if (!registrationWithSportType.sport_key) return ''

    return this.buildSportName(registrationWithSportType.sport_key)
  }

  public registrationSignupTypes(registration: Survey): string {
    const registrationWithSportType = registration as SurveyWithSportType

    return registrationWithSportType.signup_type_list?.length ? registrationWithSportType.signup_type_list.join(' / ') : ''
  }

  public formatDateRange(registration: Survey): string {
    const openDate = registration.open_date
    const closeDate = registration.close_date

    if (!openDate || !closeDate) return ''

    const dateOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions
    const format = (date: string) =>
      new Date(date).toLocaleDateString('en-US', dateOptions).replace(',', '')

    return `${format(openDate)} - ${format(closeDate)}`
  }

  public registrationInfoText(registration: Survey): string {
    return this.formatDateRange(registration)
  }

  private loadRegistrations(): void {
    this.registrationsSubscription?.unsubscribe()

    this.hasLoaded = true
    this.isLoading = true
    this.loadError = false
    this.registrationGroups = []

    this.registrationsSubscription = this.loadRegistrationGroups().subscribe({
      next: (orgs) => {
        if (this.shouldUpdateLoadedRegistrationGroups()) {
          this.loadedRegistrationGroups = orgs
          this.hasLoadedRegistrationGroups = true
        }

        this.registrationGroups = orgs.filter(
          (orgs) => orgs.registrations.length,
        )

        if (this.shouldBuildSearchData()) {
          this.buildSearchData()
          return
        }

        this.isLoading = false
      },
      error: () => {
        this.registrationGroups = []
        this.organizations = []
        this.isLoading = false
        this.loadError = true
      },
    })
  }

  private buildSearchData(): void {
    this.buildOrganizationFilterOptions()
    this.buildSportsAndSignupTypes()
    this.isLoading = false
  }

  private shouldBuildSearchData(): boolean {
    return (
      !this.organizations.length ||
      !this.sportOptions.length ||
      !this.signupTypeList.length
    )
  }

  private buildSportsAndSignupTypes(): void {
    const sports = new Map<string, OpenRegistrationFilterOption>()
    const signupTypes = new Map<string, OpenRegistrationFilterOption>()
  
    this.registrationGroups.forEach((group) => {
      group.registrations.forEach((registration) => {
        this.addRegistrationFilterOptions(
          registration as SurveyWithSportType,
          sports,
          signupTypes,
        )
      })
    })
  
    this.sportOptions = Array.from(sports.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
    this.signupTypeList = Array.from(signupTypes.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  }
  
  private addRegistrationFilterOptions(
    registration: SurveyWithSportType,
    sports: Map<string, OpenRegistrationFilterOption>,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    this.addSportOption(registration.sport_key, sports)
    this.addSignupTypeOptions(registration.signup_type_list, signupTypes)
  }
  
  private addSportOption(
    sportKey: string | undefined,
    sports: Map<string, OpenRegistrationFilterOption>,
  ): void {
    if (!sportKey || sports.has(sportKey)) return
  
    sports.set(sportKey, {
      id: sportKey,
      name: this.buildSportName(sportKey),
    })
  }
  
  private addSignupTypeOptions(
    signupTypeList: string[] | undefined,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    signupTypeList?.forEach((signupType) => {
      this.addSignupTypeOption(signupType, signupTypes)
    })
  }
  
  private addSignupTypeOption(
    signupType: string | undefined,
    signupTypes: Map<string, OpenRegistrationFilterOption>,
  ): void {
    if (!signupType || signupTypes.has(signupType)) return
  
    signupTypes.set(signupType, {
      id: signupType,
      name: signupType,
    })
  }

  private buildSportName(sport_key: string): string {
    if (!sport_key) return ''

    return sport_key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  private buildOrganizationFilterOptions(): void {
    this.organizations = this.registrationGroups
      .map((organization) => ({
        id: organization.organizationId.toString(),
        name: organization.organizationName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  private loadRegistrationGroups(): Observable<OpenRegistrationOrganization[]> {
    const selectedOrganizations = this.buildSelectedOrganizations()    
    if (selectedOrganizations.length) {
      return this.loadOrganizationRegistrations(selectedOrganizations)
    }

    if (this.hasLoadedRegistrationGroups) {
      return this.loadOrganizationRegistrations(this.loadedRegistrationGroups)
    }

    return this.siteMembershipService.mine().pipe(
      map((siteMemberships) =>
        this.buildAccessibleOrganizations(siteMemberships),
      ),
      switchMap((organizations) =>
        this.loadOrganizationRegistrations(organizations),
      ),
    )
  }

  private shouldUpdateLoadedRegistrationGroups(): boolean {
    return (
      !this.appliedOrganizationIdFilters.length &&
      !this.activeRegistrationNameLike &&
      !this.activeSports.length &&
      !this.activeTypes.length
    )
  }

  private loadOrganizationRegistrations(
    organizations: Omit<OpenRegistrationOrganization, 'registrations'>[],
  ): Observable<OpenRegistrationOrganization[]> {
    if (!organizations.length) return of([])

    return forkJoin(
      organizations.map((organization) =>
        this.surveyService
          .findAll(this.buildFindAllParams(organization.organizationId))
          .pipe(
            map((registrations) =>
              this.buildOpenRegistrationOrganization(organization, registrations),
            ),
          ),
      ),
    )
  }

  private buildOpenRegistrationOrganization(
    organization: Omit<OpenRegistrationOrganization, 'registrations'>,
    registrations: Survey[],
  ): OpenRegistrationOrganization {
    return {
      ...organization,
      organizationName:
        organization.organizationName ||
        registrations[0]?.site_name ||
        'Organization',
      registrations,
    }
  }

  private buildSelectedOrganizations(): Omit<OpenRegistrationOrganization,'registrations'>[] {
    return this.appliedOrganizationIdFilters
      .filter((organizationId) => this.hasOrganizationId(organizationId))
      .map((organizationId) => {
        const loadedOrganization = this.searchOrganizationLoaded(organizationId)
        const filterOption = this.organizations.find(
          (organization) => organization.id === organizationId.toString(),
        )

        return {
          organizationId,
          organizationName:
            loadedOrganization?.organizationName || filterOption?.name || '',
          organizationColor: loadedOrganization?.organizationColor || '',
          organizationLogoUrl: loadedOrganization?.organizationLogoUrl || '',
        }
      })
  }

  public searchOrganizationLoaded(
    organizationIdValue: number | string,
  ): Omit<OpenRegistrationOrganization, 'registrations'> | null {
    const organizationId = organizationIdValue.toString()
    const matchedGroup = this.loadedRegistrationGroups.find(
      (group) => group.organizationId.toString() === organizationId,
    )

    return matchedGroup || null
  }

  private buildAccessibleOrganizations(
    siteMemberships: SiteMembership[],
  ): Omit<OpenRegistrationOrganization, 'registrations'>[] {
    const organizations = new Map<
      number,
      Omit<OpenRegistrationOrganization, 'registrations'>
    >()

    siteMemberships.forEach((siteMembership) => {
      if (organizations.has(siteMembership.organization_id)) return

      organizations.set(siteMembership.organization_id, {
        organizationId: siteMembership.organization_id,
        organizationName: siteMembership.organization_name,
        organizationColor: siteMembership.organization_color,
        organizationLogoUrl:
          siteMembership.organization_logo?.medium_logo_url || '',
      })
    })

    return [...organizations.values()]
  }

  private buildFindAllParams(
    organizationId: string | number,
  ): SurveyFindAllParams {
    const params: SurveyFindAllParams = {
      organization_id: organizationId,
      status: SurveyStatus.Enabled,
      status_not: SurveyStatus.Archived,
      per_page: 1000,
      exclude_forms: true,
      exclude_team_only_surveys: true,
      'survey_extra[available_to_registration_list]': true,
      order_by: 'created_at',
      direction: 'desc',
      ...(this.activeRegistrationNameLike && { search: this.activeRegistrationNameLike }),
      ...(this.activeSports.length && { sports: this.activeSports }),
      ...(this.activeTypes.length && { signup_types: this.activeTypes }),
    }    
    return params
  }

  private hasOrganizationId(
    organizationId: string | number | null | undefined,
  ): organizationId is string | number {
    return (
      organizationId !== undefined &&
      organizationId !== null &&
      organizationId.toString().trim() !== ''
    )
  }

  public handleSearchTermChange(searchTerm: string): void {
    this.activeRegistrationNameLike = searchTerm
    this.searchTermSubscription?.unsubscribe()
    this.searchTermSubscription = timer(300).subscribe(() => {      
      this.loadRegistrations()
    })
  }

  public handleFiltersChange(filters: OpenRegistrationFilters): void {
    this.appliedOrganizationIdFilters = [...filters.organization]
    this.activeSports = [...filters.sport]
    this.activeTypes = [...filters.type]
    this.searchTermSubscription?.unsubscribe()
    this.loadRegistrations()
  }
}
